import { memo } from "react";
import { FeaturedProductCard } from "./FeaturedProductCard";
import { useTrustpilotRating } from "@/hooks/useTrustpilotRatings";
import { Product } from "@/contexts/ComparisonContext";
import { useProviderLiveTerms, type ProviderEntityType, type ProviderLiveTerms } from "@/hooks/useProviderLiveTerms";
import { applyLiveTermsToProduct } from "@/utils/providerLiveTerms";

interface DynamicFeaturedProductCardProps {
  product: Product;
  badge?: string;
  stats?: {
    monthlyComparisons?: string;
    avgSavings?: string;
  };
  rateLabel?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  // Trustpilot data
  entityType: ProviderEntityType;
  entitySlug: string;
  fallbackRating: number;
  fallbackReviewCount: number;
  liveTermsOverride?: ProviderLiveTerms | null;
}

/**
 * A wrapper around FeaturedProductCard that fetches dynamic Trustpilot ratings
 */
export const DynamicFeaturedProductCard = memo(({
  product,
  badge,
  stats,
  rateLabel,
  primaryButtonText,
  secondaryButtonText,
  secondaryButtonLink,
  entityType,
  entitySlug,
  fallbackRating,
  fallbackReviewCount,
  liveTermsOverride,
}: DynamicFeaturedProductCardProps) => {
  const { data: trustpilotData } = useTrustpilotRating(entityType, entitySlug);
  const shouldFetchLiveTerms = liveTermsOverride === undefined;
  const { data: fetchedLiveTerms } = useProviderLiveTerms(entityType, entitySlug, { enabled: shouldFetchLiveTerms });
  const liveTerms = shouldFetchLiveTerms ? fetchedLiveTerms : liveTermsOverride;
  const dynamicProduct = applyLiveTermsToProduct({
    ...product,
    rating: trustpilotData?.rating ?? fallbackRating,
  }, liveTerms);

  return (
    <FeaturedProductCard
      product={dynamicProduct}
      badge={badge}
      reviewCount={trustpilotData?.reviewCount ?? fallbackReviewCount}
      stats={stats}
      rateLabel={rateLabel}
      primaryButtonText={primaryButtonText}
      secondaryButtonText={secondaryButtonText}
      secondaryButtonLink={secondaryButtonLink}
    />
  );
});

DynamicFeaturedProductCard.displayName = "DynamicFeaturedProductCard";
